import { useEffect, useState } from 'react';
import { Col, FormGroup, Label, Input, Row } from 'reactstrap';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { STATE_OPTIONS, PROVINCE_OPTIONS } from '../../utils/constants';

export default function Location({ disabled, boxDbLocation, getLocationInfo, latLang, address2 }) {
  const [location, setLocation] = useState({
    name: '',
    city: '',
    state: '',
    address: '',
    address2: '',
    zip: '',
    lat: '',
    lng: '',
    country: 'United States',
    stateFieldName: 'State',
    zipFieldName: 'ZIP Code',
    stateProvinceList: STATE_OPTIONS,
  });

  useEffect(() => {
    if (boxDbLocation?.hasOwnProperty('address') && typeof boxDbLocation?.address === 'object') {
      setLocation((prevState) => ({
        ...prevState,
        city: boxDbLocation?.address?.city || '',
        state: boxDbLocation?.address?.state || '',
        address: boxDbLocation?.address?.address || '',
        address2: boxDbLocation?.address?.address2 || '',
        zip: boxDbLocation?.address?.zip || '',
        lat: boxDbLocation?.geoJson?.coordinates[1] || '',
        lng: boxDbLocation?.geoJson?.coordinates[0] || '',
      }));
    } else {
      setLocation((prevState) => ({
        ...prevState,
        city: boxDbLocation?.city || '',
        state: boxDbLocation?.state || '',
        address: boxDbLocation?.address1 || '',
        address2: boxDbLocation?.address2 || '',
        zip: boxDbLocation?.zip || '',
      }));
    }
  }, [boxDbLocation]);

  const handleChange = (address) => {
    setLocation((prevState) => ({
      ...prevState,
      address,
    }));
  };

  const checkIfStateOrProvince = (value) => {
    const province = PROVINCE_OPTIONS?.some((province) => province.value === value);
    const updatedLocation = province
      ? {
          stateFieldName: 'Province',
          zipFieldName: 'Postal Code',
          country: 'Canada',
          stateProvinceList: PROVINCE_OPTIONS,
        }
      : {
          stateFieldName: 'State',
          zipFieldName: 'ZIP Code',
          country: 'United States',
          stateProvinceList: STATE_OPTIONS,
        };

    setLocation((prevState) => ({
      ...prevState,
      ...updatedLocation,
    }));

    return province;
  };

  useEffect(() => {
    checkIfStateOrProvince(boxDbLocation?.state || boxDbLocation?.address?.state);
  }, [boxDbLocation]);

  const onAddedLocationChange = (ev) => {
    const { name, value } = ev.target;
    setLocation((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSelect = (address) => {
    const newState = { address: '', zip: 'N/A', state: 'N/A' };
    geocodeByAddress(address)
      .then((results) => {
        results[0].address_components.forEach((loc) => {
          const locType = loc.types[0];
          if (locType === 'postal_code') {
            newState.zip = loc.long_name;
          } else if (locType === 'locality') {
            newState.city = loc.long_name;
          } else if (locType === 'administrative_area_level_1') {
            newState.state = loc.short_name;
            checkIfStateOrProvince(loc.short_name);
          } else if (locType === 'street_number' || locType === 'route') {
            newState.address = newState?.address?.concat(`${loc.long_name} `);
          } else if (locType === 'country' && loc?.long_name === 'Puerto Rico') { // Puerto Rico is a territory and GEO API consider it as country but Bandwidth API consider it as state so, I'm adding this as state
            newState.state = loc.short_name;
            checkIfStateOrProvince(loc.short_name);
          }
        });
        return getLatLng(results[0]);
      })
      .then((latLng) => {
        setLocation((prevState) => ({
          ...prevState,
          ...newState,
          lat: latLng.lat,
          lng: latLng.lng,
        }));
      })
      .catch((error) => console.error('Error', error));
  };

  useEffect(() => {
    const payload = { ...location };
    delete payload.stateFieldName;
    delete payload.zipFieldName;
    delete payload.stateProvinceList;
    delete payload.name;

    if (address2) {
      delete payload.lat;
      delete payload.lng;
    }
    if (!address2) delete payload.address2;

    getLocationInfo(payload);
  }, [location]);

  const { stateFieldName, zipFieldName, country, stateProvinceList } = location;

  return (
    <>
      <PlacesAutocomplete
        value={location.address}
        onChange={handleChange}
        onSelect={handleSelect}
        searchOptions={{
          types: ['address'],
          componentRestrictions: { country: ['US', 'CA', 'PR'] }, // Restrict to US and Canada
        }}
      >
        {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
          <div>
            <FormGroup row>
              <Label for="locationId" sm={4}>
                {address2 ? 'Address 1:' : 'Address:'}
              </Label>
              <Col sm={12}>
                <Input
                  value={location.address}
                  {...getInputProps({
                    placeholder: 'Please specify',
                  })}
                />
              </Col>
            </FormGroup>
            <div>
              {loading && <div>Loading...</div>}
              {suggestions.map((suggestion) => {
                const className = suggestion.active
                  ? 'react-autosuggest__suggestion'
                  : 'react-autosuggest__suggestion';
                const style = suggestion.active
                  ? { backgroundColor: '#fafafa', cursor: 'pointer' }
                  : { backgroundColor: '#ffffff', cursor: 'pointer' };
                return (
                  <div
                    key={suggestion?.index}
                    {...getSuggestionItemProps(suggestion, {
                      className,
                      style,
                    })}
                  >
                    <span>{suggestion.description}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </PlacesAutocomplete>
      {address2 && (
        <FormGroup row>
          <Label for="name" sm={4}>
            Address 2:
          </Label>
          <Col sm={12}>
            <Input
              placeholder="Please specify"
              required
              type="text"
              name="address2"
              value={location?.address2}
              onChange={onAddedLocationChange}
            />
          </Col>
        </FormGroup>
      )}
      {latLang && (
        <Row>
          <Col sm={6}>
            <FormGroup row>
              <Label sm={12}>Latitude:</Label>
              <Col sm={12} style={{ display: 'flex', verticalAlign: 'middle' }}>
                <Input
                  placeholder="Specify Address"
                  required
                  disabled
                  type="text"
                  name="lat"
                  value={location.lat}
                  onChange={onAddedLocationChange}
                />
              </Col>
            </FormGroup>
          </Col>
          <Col sm={6}>
            <FormGroup row>
              <Label sm={12}>Longitude:</Label>
              <Col sm={12} style={{ display: 'flex', verticalAlign: 'middle' }}>
                <Input
                  placeholder="Specify Address"
                  required
                  disabled
                  type="text"
                  name="lng"
                  value={location.lng}
                  onChange={onAddedLocationChange}
                />
              </Col>
            </FormGroup>
          </Col>
        </Row>
      )}
      <FormGroup row>
        <Label for="city" sm={4}>
          City:
        </Label>
        <Col sm={12}>
          <Input
            disabled={disabled}
            placeholder="Please specify"
            required
            type="text"
            name="city"
            value={location.city}
            onChange={onAddedLocationChange}
          />
        </Col>
      </FormGroup>

      <FormGroup row>
        <Label for="state" sm={4}>
          {stateFieldName}:
        </Label>
        <Col sm={12}>
          <Input
            disabled={disabled}
            required
            type="select"
            name="state"
            value={location.state}
            onChange={onAddedLocationChange}
          >
            <option value="" hidden>
              Please specify
            </option>
            {stateProvinceList?.map(({ label, value }) => (
              <option value={value} key={value}>
                {' '}
                {label}
              </option>
            ))}
          </Input>
        </Col>
      </FormGroup>

      <FormGroup row>
        <Label for="zip" sm={4}>
          {zipFieldName}:
        </Label>
        <Col sm={12}>
          <Input
            disabled={disabled}
            placeholder="Please specify"
            required
            type="text"
            name="zip"
            value={location.zip}
            onChange={onAddedLocationChange}
          />
        </Col>
      </FormGroup>
      <FormGroup row>
        <Label for="zip" sm={4}>
          Country:
        </Label>
        <Col sm={12}>
          <Input
            disabled
            placeholder="Please specify"
            required
            type="text"
            name="zip"
            value={country}
          />
        </Col>
      </FormGroup>
    </>
  );
}
